import React, { useState, useEffect, useMemo } from 'react';
import { 
  db, 
  collection, 
  onSnapshot, 
  query,
  where,
  setDoc, 
  addDoc, 
  doc, 
  deleteDoc,
  serverTimestamp, 
  OperationType, 
  handleFirestoreError 
} from '../firebase';
import { useAuth } from '../AuthContext';
import { MultiSelectCombo } from './MultiSelectCombo';
import { motion, AnimatePresence } from 'motion/react';
import { getSafeSolicitationDocId } from '../utils/firestoreSanitizer';
import { 
  Building2, 
  MapPin, 
  Award, 
  Star, 
  Check, 
  Mail, 
  Phone, 
  User, 
  Briefcase, 
  Sparkles, 
  ArrowRight, 
  ChevronRight, 
  FileText, 
  ShieldCheck, 
  AlertTriangle,
  Zap,
  Wrench,
  Cpu,
  Compass,
  Layers,
  CheckSquare,
  Users,
  Copy,
  Download,
  Search,
  Filter,
  Plus,
  RefreshCw,
  X,
  BookOpen,
  DollarSign,
  BriefcaseIcon,
  HardDrive,
  Calendar,
  Layers2,
  Bookmark,
  Share2,
  Trash2,
  Send,
  HelpCircle,
  TrendingUp,
  FileSpreadsheet
} from 'lucide-react';
import { jsPDF } from 'jspdf';

// Federal RFP Item Schema
export interface FederalRfx {
  id: string;
  solicitationNumber: string;
  title: string;
  agency: string;
  subAgency: string;
  agencyType: 'Civilian' | 'Military';
  postedDate: string;
  responseDeadDate: string;
  naicsCode: string;
  pscCode: string;
  setAsideType: 'Total Small Business' | '8(a)' | 'HUBZone' | 'SDVOSB' | 'WOSB' | 'None';
  estimatedValue: number;
  description: string;
  placeOfPerformance: string;
  contactName: string;
  contactEmail: string;
  contactPhone: string;
  sourcingSource: 'SAM.gov' | 'Military Procurement Command' | 'Pavilion' | 'Omnia Partners';
}

// Preseeded Federal & Co-op Opportunities
const PRESEEDED_FEDERAL_RFXS: FederalRfx[] = [
  {
    id: 'fed_rfp_001',
    solicitationNumber: 'N0018926R0012',
    title: 'Naval Station Norfolk Tactical Communications Infrastructure Modernization',
    agency: 'Department of the Navy',
    subAgency: 'Naval Supply Systems Command (NAVSUP)',
    agencyType: 'Military',
    postedDate: '2026-06-15',
    responseDeadDate: '2026-08-10',
    naicsCode: '541512',
    pscCode: '7G21',
    setAsideType: 'Total Small Business',
    estimatedValue: 1250000,
    description: 'Procurement and integration of advanced tactical communication networks, fiber optic cabling links, and secure system switches for Naval Station Norfolk maritime logistics hubs. Requires NIST SP 800-171 cybersecurity compliance audit.',
    placeOfPerformance: 'Norfolk, VA',
    contactName: 'Lt. Commander Sarah K. Vance',
    contactEmail: 'sarah.k.vance@navy.mil',
    contactPhone: '(757) 555-0144',
    sourcingSource: 'Military Procurement Command'
  },
  {
    id: 'fed_rfp_002',
    solicitationNumber: '80HQTR26R0018',
    title: 'NASA Goddard Space Flight Center Cloud Engineering & Identity Management Support',
    agency: 'National Aeronautics and Space Administration',
    subAgency: 'Goddard Space Flight Center',
    agencyType: 'Civilian',
    postedDate: '2026-06-20',
    responseDeadDate: '2026-07-30',
    naicsCode: '541511',
    pscCode: 'DA01',
    setAsideType: '8(a)',
    estimatedValue: 3400000,
    description: 'Provide end-to-end multi-cloud engineering, zero-trust identity and access management (ICAM), and FedRAMP compliance documentation support services for Goddard scientific computing teams.',
    placeOfPerformance: 'Greenbelt, MD',
    contactName: 'Dr. Alistair Sterling',
    contactEmail: 'alistair.sterling@nasa.gov',
    contactPhone: '(301) 555-0199',
    sourcingSource: 'SAM.gov'
  },
  {
    id: 'fed_rfp_003',
    solicitationNumber: 'DHS-OIG-26-0044',
    title: 'FEMA Regional Headquarters Energy Resilience Audits & Emergency Solar Design',
    agency: 'Department of Homeland Security',
    subAgency: 'Federal Emergency Management Agency (FEMA)',
    agencyType: 'Civilian',
    postedDate: '2026-06-10',
    responseDeadDate: '2026-07-25',
    naicsCode: '541690',
    pscCode: 'R425',
    setAsideType: 'HUBZone',
    estimatedValue: 750000,
    description: 'Technical audits of backup electrical generators, emergency microgrid layouts, and detailed engineering plans for roof-mounted solar PV storage arrays across ten FEMA regional command depots.',
    placeOfPerformance: 'Denver, CO',
    contactName: 'Monica Diaz-Alvarez',
    contactEmail: 'monica.diaz@fema.dhs.gov',
    contactPhone: '(202) 555-0120',
    sourcingSource: 'SAM.gov'
  },
  {
    id: 'fed_rfp_004',
    solicitationNumber: 'W9128F26B0089',
    title: 'Fort Bliss Battalion Barracks Structural Rehabilitation & Cool Roofing',
    agency: 'Department of the Army',
    subAgency: 'U.S. Army Corps of Engineers (USACE)',
    agencyType: 'Military',
    postedDate: '2026-06-05',
    responseDeadDate: '2026-08-01',
    naicsCode: '236220',
    pscCode: 'Z1EB',
    setAsideType: 'SDVOSB',
    estimatedValue: 4800000,
    description: 'Comprehensive design-build structural repairs, thermal window replacements, high-reflectance cool roofing installation, and HVAC damper upgrades across multiple infantry battalion living quarters.',
    placeOfPerformance: 'Fort Bliss, TX',
    contactName: 'Marcus T. Brodie, USACE contracting officer',
    contactEmail: 'marcus.t.brodie@usace.army.mil',
    contactPhone: '(915) 555-0162',
    sourcingSource: 'Military Procurement Command'
  },
  {
    id: 'fed_rfp_005',
    solicitationNumber: 'DE-SOL-0010482',
    title: 'Department of Energy National Labs Cyber Range & Threat Vector Simulator',
    agency: 'Department of Energy',
    subAgency: 'Office of Science & Cyber Security',
    agencyType: 'Civilian',
    postedDate: '2026-06-25',
    responseDeadDate: '2026-08-20',
    naicsCode: '541519',
    pscCode: 'DJ10',
    setAsideType: 'WOSB',
    estimatedValue: 1850000,
    description: 'Design, configuration, and implementation of a virtual cyber-range simulation platform to test critical infrastructure control systems (ICS/SCADA) against emerging state-sponsored digital threats.',
    placeOfPerformance: 'Oak Ridge, TN',
    contactName: 'Elizabeth Vance',
    contactEmail: 'elizabeth.vance@science.doe.gov',
    contactPhone: '(865) 555-0111',
    sourcingSource: 'SAM.gov'
  },
  {
    id: 'coop_001',
    solicitationNumber: 'OMNIA-2026-89B',
    title: 'National Joint Cooperative Purchasing Contract for Energy Efficiency Retrofits',
    agency: 'Omnia Partners (Cooperative)',
    subAgency: 'Lead Public Agency: Region 4 Education Service Center',
    agencyType: 'Civilian',
    postedDate: '2026-05-12',
    responseDeadDate: '2027-05-12',
    naicsCode: '238210',
    pscCode: 'F999',
    setAsideType: 'None',
    estimatedValue: 15000000,
    description: 'A pre-negotiated master national cooperative contract allowing public universities, cities, counties, and K-12 school districts to purchase energy audit software, LED lightning grids, and heat-pump systems without conducting separate formal local RFP solicitations.',
    placeOfPerformance: 'Nationwide (Piggybacking Clause)',
    contactName: 'Patricia Geller',
    contactEmail: 'coopsourcing@omniapartners.com',
    contactPhone: '(800) 555-0100',
    sourcingSource: 'Omnia Partners'
  },
  {
    id: 'coop_002',
    solicitationNumber: 'PAV-FAC-2026-104',
    title: 'Multi-State Co-op Water Infrastructure Abatement, Testing & Pipe Replacement',
    agency: 'Pavilion Cooperative',
    subAgency: 'Lead Public Agency: Kansas City Metropolitan Co-op Union',
    agencyType: 'Civilian',
    postedDate: '2026-04-18',
    responseDeadDate: '2027-04-18',
    naicsCode: '237110',
    pscCode: 'F108',
    setAsideType: 'None',
    estimatedValue: 8900000,
    description: 'Piggybackable water filtration, soil sampling, heavy metal testing, and modular copper line replacement solutions. Sourced for small municipalities looking to utilize federal Infrastructure Investment funds rapidly.',
    placeOfPerformance: 'Multi-State Regional',
    contactName: 'Robert Vance Jr.',
    contactEmail: 'alliances@pavilioncoop.org',
    contactPhone: '(816) 555-0187',
    sourcingSource: 'Pavilion'
  }
];

// CRM Contact Schema
export interface RfxContact {
  id: string;
  name: string;
  email: string;
  phone: string;
  agency: string;
  sourceRfxTitle: string;
  sourceRfxNumber: string;
  notes: string;
  createdBy: string;
  createdAt: string;
}

export const FederalPortalView: React.FC = () => {
  const { user } = useAuth();

  // Active Tab
  const [currentTab, setCurrentTab] = useState<'rfx_search' | 'sam_register' | 'standards_exp' | 'crm_contacts'>('rfx_search');

  // Search/Filter states
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedSources, setSelectedSources] = useState<string[]>([]);
  const [selectedSetAsides, setSelectedSetAsides] = useState<string[]>([]);
  const [selectedAgencyTypes, setSelectedAgencyTypes] = useState<string[]>([]);

  // State arrays
  const [federalOpportunities, setFederalOpportunities] = useState<FederalRfx[]>(PRESEEDED_FEDERAL_RFXS);
  const [crmContacts, setCrmContacts] = useState<RfxContact[]>([]);
  const [trackingLoading, setTrackingLoading] = useState(false);
  const [contactsLoading, setContactsLoading] = useState(true);

  // CRM message modal state
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [selectedCrmContact, setSelectedCrmContact] = useState<RfxContact | null>(null);
  const [emailSubject, setEmailSubject] = useState('');
  const [emailBody, setEmailBody] = useState('');
  const [sendingEmail, setSendingEmail] = useState(false);

  // SAM Registration Wizard State
  const [samStep, setSamStep] = useState(1);
  const [samForm, setSamForm] = useState({
    legalName: '',
    ein: '',
    address: '',
    city: '',
    state: 'California',
    zip: '',
    eftRouting: '',
    eftAccount: '',
    primaryNaics: '541511',
    annualReceipts: '',
    employeesCount: '',
    certifiedWosb: false,
    certifiedSdvosb: false,
    certifiedHubzone: false,
    certified8a: false,
  });
  const [samSaved, setSamSaved] = useState(false);

  // NAICS Size standards state
  const [calcNaics, setCalcNaics] = useState('541511');
  const [calcReceipts, setCalcReceipts] = useState('4500000');
  const [calcEmployees, setCalcEmployees] = useState('18');
  const [sizeResult, setSizeResult] = useState<{ isSmall: boolean; standardLimit: string; explanation: string } | null>(null);

  // Piggybacking Proposal Agreement draft State
  const [showPiggybackModal, setShowPiggybackModal] = useState(false);
  const [selectedCoopRfx, setSelectedCoopRfx] = useState<FederalRfx | null>(null);
  const [vendorCompany, setVendorCompany] = useState('');
  const [procuringAgency, setProcuringAgency] = useState('');
  const [piggybackDraft, setPiggybackDraft] = useState('');

  // Sourcing dropdown filters
  const SOURCING_SOURCES = ['All', 'SAM.gov', 'Military Procurement Command', 'Pavilion', 'Omnia Partners'];
  const SET_ASIDE_TYPES = ['All', 'Total Small Business', '8(a)', 'HUBZone', 'SDVOSB', 'WOSB', 'None'];

  const sourcingOptions = useMemo(() => SOURCING_SOURCES.filter(s => s !== 'All').map(s => ({ value: s, label: s })), []);
  const setAsideOptions = useMemo(() => SET_ASIDE_TYPES.filter(s => s !== 'All').map(s => ({ value: s, label: s })), []);
  const agencyTypeOptions = useMemo(() => [
    { value: 'Military', label: 'Military & Defense Command' },
    { value: 'Civilian', label: 'Civilian Federal Agencies' }
  ], []);

  // Common NAICS & PSC Codes Reference database
  const POPULAR_NAICS = [
    { code: '541511', title: 'Custom Computer Programming Services', sizeStandard: '$34.0M' },
    { code: '541512', title: 'Computer Systems Design Services', sizeStandard: '$34.0M' },
    { code: '541519', title: 'Other Computer Related Services', sizeStandard: '$34.0M or 150 employees' },
    { code: '236220', title: 'Commercial and Institutional Building Construction', sizeStandard: '$45.0M' },
    { code: '238210', title: 'Electrical Contractors and Other Wiring Installation', sizeStandard: '$19.0M' },
    { code: '238220', title: 'Plumbing, Heating, and Air-Conditioning Contractors', sizeStandard: '$19.0M' },
    { code: '541330', title: 'Engineering Services (Structural/Military/Civil)', sizeStandard: '$25.5M' },
    { code: '541620', title: 'Environmental Consulting Services', sizeStandard: '$19.0M' },
    { code: '561621', title: 'Security Systems Services (Lock/Alarms/CCTV)', sizeStandard: '$25.0M' }
  ];

  const POPULAR_PSC = [
    { code: '7G21', title: 'IT and Telecom - Network devices / router systems' },
    { code: 'DA01', title: 'IT and Telecom - Cloud support & platform infrastructure' },
    { code: 'DJ10', title: 'IT and Telecom - Security and compliance services' },
    { code: 'Z1EB', title: 'Maintenance of barracks / military living quarters' },
    { code: 'R425', title: 'Support - Professional: engineering & energy technical audits' },
    { code: 'F108', title: 'Environmental system cleaning and lead abatement' },
    { code: 'F999', title: 'Other environmental reclamation/remedial services' }
  ];

  // Load Contacts list from Firestore CRM
  useEffect(() => {
    if (!user) return;
    setContactsLoading(true);

    const q = query(collection(db, 'rfx_contacts'), where('createdBy', '==', user.uid));
    const unsub = onSnapshot(q, (snapshot) => {
      const list: RfxContact[] = [];
      snapshot.forEach((doc) => {
        const data = doc.data();
        list.push({ id: doc.id, ...data } as RfxContact);
      });
      setCrmContacts(list);
      setContactsLoading(false);
    }, (err) => {
      console.error("Error subscribing to rfx_contacts:", err);
      setContactsLoading(false);
    });

    return () => unsub();
  }, [user]);

  // Load registered profile to prefill SAM registration wizard
  useEffect(() => {
    if (!user) return;
    const unsub = onSnapshot(doc(db, 'vendors', user.uid), (snap) => {
      if (snap.exists()) {
        const vData = snap.data();
        setSamForm(prev => ({
          ...prev,
          legalName: vData.companyName || prev.legalName,
          address: vData.address || prev.address,
          city: vData.city || prev.city,
          state: vData.state || prev.state,
          zip: vData.zip || prev.zip,
          primaryNaics: vData.primaryNaics || vData.naics || prev.primaryNaics,
          annualReceipts: vData.annualRevenue || prev.annualReceipts,
          employeesCount: vData.employees || prev.employeesCount,
          certifiedWosb: vData.certifications?.includes('WOSB') || prev.certifiedWosb,
          certifiedSdvosb: vData.certifications?.includes('SDVOSB') || prev.certifiedSdvosb,
          certifiedHubzone: vData.certifications?.includes('HUBZone') || prev.certifiedHubzone,
          certified8a: vData.certifications?.includes('8a') || prev.certified8a,
        }));
      }
    });
    return () => unsub();
  }, [user]);

  // Filter Federal Solicitations
  const filteredRfxs = useMemo(() => {
    return federalOpportunities.filter((item) => {
      if (!item) return false;
      const title = item.title || '';
      const solNum = item.solicitationNumber || '';
      const naics = item.naicsCode || '';
      const psc = item.pscCode || '';
      const agency = item.agency || '';
      const desc = item.description || '';
      
      const matchesSearch = 
        title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        solNum.toLowerCase().includes(searchQuery.toLowerCase()) ||
        naics.toLowerCase().includes(searchQuery.toLowerCase()) ||
        psc.toLowerCase().includes(searchQuery.toLowerCase()) ||
        agency.toLowerCase().includes(searchQuery.toLowerCase()) ||
        desc.toLowerCase().includes(searchQuery.toLowerCase());

      const matchesSource = selectedSources.length === 0 || (item.sourcingSource && selectedSources.includes(item.sourcingSource));
      const matchesSetAside = selectedSetAsides.length === 0 || (item.setAsideType && selectedSetAsides.includes(item.setAsideType));
      const matchesAgencyType = selectedAgencyTypes.length === 0 || (item.agencyType && selectedAgencyTypes.includes(item.agencyType));

      return matchesSearch && matchesSource && matchesSetAside && matchesAgencyType;
    });
  }, [federalOpportunities, searchQuery, selectedSources, selectedSetAsides, selectedAgencyTypes]);

  // Extract contracting point of contact and save to CRM Contacts database
  const handleExtractContact = async (item: FederalRfx) => {
    if (!user) {
      alert("Please register or login to save RFP contacts in your CRM list.");
      return;
    }

    if (!item.contactName || !item.contactEmail) {
      alert("No point of contact information was provided in the solicitation metadata to extract.");
      return;
    }

    // Check if already extracted to avoid duplicates
    const alreadyExists = crmContacts.some(
      c => c.email.toLowerCase() === item.contactEmail.toLowerCase() && c.sourceRfxNumber === item.solicitationNumber
    );

    if (alreadyExists) {
      alert(`Point of contact from RFP ${item.solicitationNumber} is already saved inside your Contacts CRM database.`);
      return;
    }

    const contactId = `contact_${Date.now()}`;
    const payload: RfxContact = {
      id: contactId,
      name: item.contactName,
      email: item.contactEmail,
      phone: item.contactPhone || '(N/A)',
      agency: item.agency,
      sourceRfxTitle: item.title,
      sourceRfxNumber: item.solicitationNumber,
      notes: `Extracted automatically from ${item.sourcingSource} RFP opportunity.`,
      createdBy: user.uid,
      createdAt: new Date().toISOString()
    };

    try {
      await setDoc(doc(db, 'rfx_contacts', contactId), payload);
      alert(`Successfully Extracted CRM Contact! Added "${item.contactName}" (${item.agency}) to your local Procurement CRM.`);
    } catch (err) {
      console.error("Error creating CRM contact:", err);
      handleFirestoreError(err, OperationType.CREATE, `rfx_contacts/${contactId}`);
    }
  };

  // Delete a CRM contact
  const handleDeleteContact = async (id: string) => {
    if (!confirm("Are you sure you want to remove this point of contact from your CRM?")) return;
    try {
      await deleteDoc(doc(db, 'rfx_contacts', id));
    } catch (err) {
      console.error("Could not delete CRM contact:", err);
    }
  };

  // Save the SAM.gov Registration profile
  const handleSaveSamRegistration = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;

    try {
      // Form fields saved to vendor profile as metadata
      const certs: string[] = [];
      if (samForm.certifiedWosb) certs.push('WOSB');
      if (samForm.certifiedSdvosb) certs.push('SDVOSB');
      if (samForm.certifiedHubzone) certs.push('HUBZone');
      if (samForm.certified8a) certs.push('8a');

      await setDoc(doc(db, 'vendors', user.uid), {
        companyName: samForm.legalName,
        ein: samForm.ein,
        address: samForm.address,
        city: samForm.city,
        state: samForm.state,
        zip: samForm.zip,
        primaryNaics: samForm.primaryNaics,
        certifications: certs,
        samStatus: 'In-Progress (Ready for Submission)',
        lastUpdated: serverTimestamp()
      }, { merge: true });

      setSamSaved(true);
      setTimeout(() => setSamSaved(false), 4000);
    } catch (err) {
      console.error("Error saving SAM details:", err);
    }
  };

  // Generate SAM Compliance Assessment Report PDF
  const handleDownloadSAMReport = () => {
    const docPdf = new jsPDF();
    docPdf.setFont("helvetica", "normal");

    docPdf.setFillColor(30, 41, 59); // Slate-800
    docPdf.rect(0, 0, 210, 45, 'F');

    docPdf.setTextColor(255, 255, 255);
    docPdf.setFontSize(22);
    docPdf.text("SAM.GOV REGISTRATION ASSESSMENT", 15, 20);
    docPdf.setFontSize(10);
    docPdf.text(`Generated for ${samForm.legalName || 'Unregistered Vendor'} on ${new Date().toLocaleDateString()}`, 15, 30);

    docPdf.setTextColor(30, 41, 59);
    docPdf.setFontSize(13);
    docPdf.setFont("helvetica", "bold");
    docPdf.text("1. Legal Entity & Tax Information Summary", 15, 55);

    docPdf.setFont("helvetica", "normal");
    docPdf.setFontSize(10);
    docPdf.text(`Legal Entity Name: ${samForm.legalName || 'N/A'}`, 15, 65);
    docPdf.text(`Employer Identification Number (EIN): ${samForm.ein || 'N/A'}`, 15, 72);
    docPdf.text(`Primary Address: ${samForm.address}, ${samForm.city}, ${samForm.state} ${samForm.zip}`, 15, 79);

    docPdf.setFont("helvetica", "bold");
    docPdf.setFontSize(13);
    docPdf.text("2. EFT & Financial Remittance Credentials", 15, 92);
    docPdf.setFont("helvetica", "normal");
    docPdf.setFontSize(10);
    docPdf.text(`Direct Deposit Routing Number: ${samForm.eftRouting ? '••••' + samForm.eftRouting.slice(-4) : 'Pending'}`, 15, 102);
    docPdf.text(`Remittance Bank Account: ${samForm.eftAccount ? '••••••••' + samForm.eftAccount.slice(-4) : 'Pending'}`, 15, 109);

    docPdf.setFont("helvetica", "bold");
    docPdf.setFontSize(13);
    docPdf.text("3. SBA Diversity Certifications Claimed", 15, 122);
    docPdf.setFont("helvetica", "normal");
    docPdf.setFontSize(10);
    let certStr = [];
    if (samForm.certifiedWosb) certStr.push('Women-Owned Small Business (WOSB)');
    if (samForm.certifiedSdvosb) certStr.push('Service-Disabled Veteran-Owned (SDVOSB)');
    if (samForm.certifiedHubzone) certStr.push('HUBZone Certified');
    if (samForm.certified8a) certStr.push('8(a) Joint Venture/Business Dev Program');
    if (certStr.length === 0) certStr.push('Small Business Standard Only (No Diversity Set-Asides)');
    
    certStr.forEach((cert, idx) => {
      docPdf.text(`• ${cert}`, 18, 132 + (idx * 7));
    });

    docPdf.setFont("helvetica", "bold");
    docPdf.setFontSize(13);
    docPdf.text("4. Step-by-Step SAM.gov Action Checklist", 15, 170);
    docPdf.setFont("helvetica", "normal");
    docPdf.setFontSize(10);
    docPdf.text("[X] Step 1: Obtain Employer Identification Number (EIN) from the IRS.", 15, 180);
    docPdf.text("[X] Step 2: Formulate legal operating entity name matching state tax division registers.", 15, 187);
    docPdf.text("[ ] Step 3: File digital application on SAM.gov portal for Unique Entity ID (UEI).", 15, 194);
    docPdf.text("[ ] Step 4: Secure physical address validation document to satisfy notarized letter rules.", 15, 201);
    docPdf.text("[ ] Step 5: Receive 5-character CAGE Code verification via Defense Logistics Agency (DLA).", 15, 208);

    docPdf.line(15, 222, 195, 222);
    docPdf.setFontSize(9);
    docPdf.text("RFXpress Portal - Federal Acquisition Compliance & Research Suite", 15, 232);

    docPdf.save(`SAM_Readiness_Report_${samForm.legalName.replace(/\s+/g, '_') || 'Vendor'}.pdf`);
  };

  // Size Standards Calculator
  const handleCalculateSizeStandard = () => {
    const receiptsVal = Number(calcReceipts) || 0;
    const employeesVal = Number(calcEmployees) || 0;

    let isSmall = true;
    let limitStr = '';
    let explanation = '';

    if (calcNaics === '541511' || calcNaics === '541512' || calcNaics === '541519') {
      limitStr = '$34.0 Million (3-Year Avg Receipts)';
      isSmall = receiptsVal <= 34000000;
      explanation = isSmall 
        ? `Your average revenue ($${(receiptsVal/1000000).toFixed(2)}M) is below the SBA size limit of $34.0M. You qualify as a Small Business for computer and custom software engineering RFPs.` 
        : `Your average revenue exceeds the $34.0M standard. You are classified as a Large Business under this NAICS, but may still joint-venture.`;
    } else if (calcNaics === '236220') {
      limitStr = '$45.0 Million (3-Year Avg Receipts)';
      isSmall = receiptsVal <= 45000000;
      explanation = isSmall
        ? `Your average revenue is below the SBA size limit of $45.0M for Commercial Construction. You qualify as a Small Business.`
        : `Your average revenue exceeds $45.0M. You do not qualify for Small Business set-asides in commercial building construction.`;
    } else if (calcNaics === '238210' || calcNaics === '238220') {
      limitStr = '$19.0 Million (3-Year Avg Receipts)';
      isSmall = receiptsVal <= 19000000;
      explanation = isSmall
        ? `Your average revenue is below the SBA limit of $19.0M. You qualify as a Small Business electrical/mechanical contractor.`
        : `Your average revenue exceeds $19.0M. You must bid as a Large Business or partner under a sub-agreement.`;
    } else if (calcNaics === '541330') {
      limitStr = '$25.5 Million (3-Year Avg Receipts)';
      isSmall = receiptsVal <= 25500000;
      explanation = isSmall
        ? `Your average revenue is below the engineering limit of $25.5M. You are classified as a Small Business.`
        : `Your average revenue exceeds the $25.5M standard for professional engineering services.`;
    } else {
      limitStr = '$19.0 Million (Standard)';
      isSmall = receiptsVal <= 19000000;
      explanation = `Using standard SBA average fallback of $19.0M. Your business is ${isSmall ? 'eligible' : 'not eligible'} for small business preferences.`;
    }

    setSizeResult({ isSmall, standardLimit: limitStr, explanation });
  };

  // Track opportunities as solicitation
  const handleTrackOpportunity = async (item: FederalRfx) => {
    if (!user) {
      alert("Please login first to track solicitations.");
      return;
    }
    setTrackingLoading(true);

    try {
      // Insert into saved_solicitations
      const savedId = `${user.uid}_${getSafeSolicitationDocId(item.solicitationNumber)}`;
      await setDoc(doc(db, 'saved_solicitations', savedId), {
        id: savedId,
        savedId: savedId,
        userId: user.uid,
        userUid: user.uid,
        solicitationId: item.solicitationNumber,
        title: item.title,
        agency: item.agency,
        state: item.placeOfPerformance || 'N/A',
        dueDate: item.responseDeadDate || '',
        amount: item.estimatedValue || 0,
        description: item.description || '',
        notes: `Tracked from Federal & Co-op Center (${item.sourcingSource}).`,
        trackedAt: new Date().toISOString(),
        reminderDaysBefore: 3,
        createdAt: new Date().toISOString()
      });
      alert(`Success! Tracked opportunity "${item.solicitationNumber}" has been saved to your primary RFP Responses tab.`);
    } catch (err) {
      console.error("Error saving solicitation:", err);
    } finally {
      setTrackingLoading(false);
    }
  };

  // Launch Piggybacking Agreement Draft
  const handleOpenPiggybackDraft = (item: FederalRfx) => {
    setSelectedCoopRfx(item);
    setVendorCompany(samForm.legalName || 'Vendor Company Inc.');
    setProcuringAgency('Sunnyvale Public School District (or Local Municipality)');
    
    const text = `COOPERATIVE PIGGYBACKING PURCHASING AGREEMENT PROPOSAL

DATE: ${new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
LEAD COOPERATIVE SYSTEM: ${item.agency}
REFERENCE CONTRACT/RFP ID: ${item.solicitationNumber}
CONTRACT TITLE: ${item.title}
CONTRACT VALUE: $${item.estimatedValue.toLocaleString()}

PARTIES:
1. Vendor / Contractor: [Vendor Company Name]
2. Procuring Local Government Agency: [Procuring Agency Name]

=== COOPERATIVE PROCUREMENT RECITALS ===
WHEREAS, the Lead Cooperative System conducted a full, formal, competitive, public solicitation bidding procedure matching all federal, state, and county transparency guidelines; and

WHEREAS, the Contract incorporates a standard "Cooperative Piggybacking Clause" allowing any municipal agency, state educational center, or qualified public utility in the United States to purchase goods or services under the pre-negotiated volume price schedule; and

WHEREAS, the Procuring Local Government Agency desires to utilize the pricing and contract structures from the Reference Contract, bypassing the expense, delay, and administrative burden of launching an independent local RFP;

NOW, THEREFORE, the Parties agree as follows:
1. Scope of Works: The Vendor shall provide the technical deliverables specified in reference RFP ${item.solicitationNumber} directly to the Procuring Local Government Agency.
2. Pricing Schedules: Pricing shall strictly match the pre-negotiated national co-op rate sheet, subject to a local volume discount.
3. Administrative Reporting: The Vendor shall report all sales figures back to the cooperative contract oversight board.

IN WITNESS WHEREOF:
Authorized Signature (Vendor Representative): _______________________
Authorized Signature (Procuring Officer): _______________________`;

    setPiggybackDraft(text);
    setShowPiggybackModal(true);
  };

  // Export Piggyback Proposal Agreement
  const handleExportPiggybackPDF = () => {
    const docPdf = new jsPDF();
    docPdf.setFont("helvetica", "normal");
    docPdf.setFontSize(16);
    docPdf.text("COOPERATIVE PIGGYBACK AGREEMENT DRAFT", 20, 20);
    docPdf.setFontSize(10);
    docPdf.text(`Exported from RFXpress Portal on ${new Date().toLocaleDateString()}`, 20, 27);
    docPdf.line(20, 31, 190, 31);
    
    docPdf.setFontSize(10);
    // Resolve placeholders in template before saving
    let resolvedText = piggybackDraft
      .replace("[Vendor Company Name]", vendorCompany)
      .replace("[Procuring Agency Name]", procuringAgency);
    
    const splitText = docPdf.splitTextToSize(resolvedText, 170);
    docPdf.text(splitText, 20, 40);
    
    docPdf.save(`Coop_Piggyback_${selectedCoopRfx?.solicitationNumber || 'Proposal'}.pdf`);
  };

  // Launch Gmail Sender
  const handleLaunchEmail = (contact: RfxContact) => {
    setSelectedCrmContact(contact);
    setEmailSubject(`Inquiry: RFP Solicitation #${contact.sourceRfxNumber} - Capabilities statement`);
    setEmailBody(`Dear ${contact.name},\n\nOur company, ${samForm.legalName || '[My Company]'}, is actively tracking public RFP "${contact.sourceRfxTitle}" (Sol. #${contact.sourceRfxNumber}) issued by ${contact.agency}.\n\nAs a certified supplier, we would like to submit a formal capability statement. Could you please confirm if there are any specific local preference programs or small business participation quotas we can address?\n\nSincerely,\n\n[My Contact Name]\n${samForm.legalName || 'Supplier Co.'}`);
    setShowEmailModal(true);
  };

  // Send Email simulated
  const handleSendEmail = () => {
    if (!emailSubject || !emailBody) return;
    setSendingEmail(true);
    setTimeout(() => {
      setSendingEmail(false);
      setShowEmailModal(false);
      alert(`Email dispatched successfully to ${selectedCrmContact?.email}! A log record has been cataloged in your communications history.`);
    }, 1500);
  };

  return (
    <div id="federal-portal-workspace" className="space-y-8">
      {/* Header and description */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-extrabold text-gray-900 tracking-tight flex items-center gap-3">
            <ShieldCheck className="text-indigo-600 w-8 h-8" />
            Federal &amp; Cooperative Procurement Portal
          </h1>
          <p className="text-gray-500 mt-1 max-w-2xl">
            Pursue high-value military bids, civilian agency RFPs, and national purchasing cooperatives (Omnia Partners &amp; Pavilion). Secure your UEI, CAGE code, and extract target points of contact directly to your local CRM.
          </p>
        </div>
        
        {/* Navigation Selector */}
        <div className="flex items-center gap-2 bg-gray-100 p-1 rounded-xl self-start">
          <button
            onClick={() => setCurrentTab('rfx_search')}
            className={`px-4 py-2 rounded-lg text-xs font-semibold transition-all ${
              currentTab === 'rfx_search' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-900'
            }`}
          >
            Federal &amp; Co-op Bids
          </button>
          <button
            onClick={() => setCurrentTab('sam_register')}
            className={`px-4 py-2 rounded-lg text-xs font-semibold transition-all ${
              currentTab === 'sam_register' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-900'
            }`}
          >
            SAM.gov Registration Guide
          </button>
          <button
            onClick={() => setCurrentTab('standards_exp')}
            className={`px-4 py-2 rounded-lg text-xs font-semibold transition-all ${
              currentTab === 'standards_exp' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-900'
            }`}
          >
            NAICS &amp; Size Standards
          </button>
          <button
            onClick={() => setCurrentTab('crm_contacts')}
            className={`px-4 py-2 rounded-lg text-xs font-semibold transition-all flex items-center gap-1.5 ${
              currentTab === 'crm_contacts' ? 'bg-white text-indigo-700 shadow-sm' : 'text-gray-500 hover:text-indigo-700'
            }`}
          >
            CRM Contacts
            {crmContacts.length > 0 && (
              <span className="bg-indigo-100 text-indigo-800 text-[10px] font-bold px-1.5 py-0.5 rounded-full">
                {crmContacts.length}
              </span>
            )}
          </button>
        </div>
      </div>

      {/* Primary Panels rendering inside tabs */}
      <AnimatePresence mode="wait">
        {currentTab === 'rfx_search' && (
          <motion.div
            key="fed-rfx-search"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.12 }}
            className="space-y-6"
          >
            {/* Filter controls */}
            <div className="bg-white p-5 rounded-2xl border border-gray-150 shadow-sm space-y-4">
              <div className="flex flex-col lg:flex-row gap-4">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
                  <input
                    type="text"
                    placeholder="Search by solicitation number, agency, title, NAICS or description..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full bg-gray-50 hover:bg-gray-100/50 focus:bg-white focus:ring-2 focus:ring-indigo-100 focus:border-indigo-600 border border-gray-200 rounded-xl pl-10 pr-4 py-2.5 text-sm transition-all outline-none"
                  />
                  {searchQuery && (
                    <button onClick={() => setSearchQuery('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                      <X className="w-4 h-4" />
                    </button>
                  )}
                </div>

                <div className="grid grid-cols-3 gap-3 items-center">
                  <div>
                    <MultiSelectCombo
                      options={sourcingOptions}
                      selectedValues={selectedSources}
                      onChange={setSelectedSources}
                      placeholder="All Sourcing Conduits"
                      icon={<Briefcase className="w-3.5 h-3.5" />}
                    />
                  </div>

                  <div>
                    <MultiSelectCombo
                      options={setAsideOptions}
                      selectedValues={selectedSetAsides}
                      onChange={setSelectedSetAsides}
                      placeholder="All Set-Asides"
                      icon={<Award className="w-3.5 h-3.5" />}
                    />
                  </div>

                  <div>
                    <MultiSelectCombo
                      options={agencyTypeOptions}
                      selectedValues={selectedAgencyTypes}
                      onChange={setSelectedAgencyTypes}
                      placeholder="All Agencies"
                      icon={<Building2 className="w-3.5 h-3.5" />}
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Sourcing Results Grid */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {filteredRfxs.map((item) => (
                <div 
                  key={item.id}
                  className="bg-white border border-gray-150 rounded-2xl shadow-sm p-6 flex flex-col justify-between hover:border-indigo-200 transition-all relative"
                >
                  <div className="absolute top-6 right-6 flex items-center gap-1.5">
                    <span className={`text-[9px] px-2.5 py-0.5 font-extrabold uppercase rounded-full tracking-wider border ${
                      (item.sourcingSource || '').includes('Military') ? 'bg-amber-50 text-amber-800 border-amber-200' :
                      (item.sourcingSource || '').includes('SAM') ? 'bg-blue-50 text-blue-800 border-blue-200' :
                      'bg-purple-50 text-purple-800 border-purple-200'
                    }`}>
                      {item.sourcingSource}
                    </span>
                  </div>

                  <div className="space-y-4">
                    {/* Upper title metadata */}
                    <div className="space-y-1.5">
                      <div className="flex items-center gap-2 text-xs font-mono text-gray-400">
                        <span>Sol. #{item.solicitationNumber}</span>
                        <span>&bull;</span>
                        <span>Due: {item.responseDeadDate}</span>
                      </div>
                      <h3 className="text-base font-black text-gray-900 pr-24 leading-snug">
                        {item.title}
                      </h3>
                      <p className="text-xs text-gray-500 font-medium flex items-center gap-1">
                        <Building2 className="w-3.5 h-3.5 text-indigo-500" />
                        {item.agency} {item.subAgency && `(${item.subAgency})`}
                      </p>
                    </div>

                    {/* SOW description */}
                    <p className="text-xs text-gray-500 leading-relaxed line-clamp-3">
                      {item.description}
                    </p>

                    {/* Key Attributes Grid */}
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 pt-2 text-[10px] text-gray-500 font-medium">
                      <div className="bg-gray-50 p-2 rounded-xl">
                        <p className="text-gray-400 uppercase text-[8px] font-bold tracking-wider">NAICS Code</p>
                        <p className="text-gray-800 font-mono font-bold mt-0.5">{item.naicsCode}</p>
                      </div>
                      <div className="bg-gray-50 p-2 rounded-xl">
                        <p className="text-gray-400 uppercase text-[8px] font-bold tracking-wider">PSC Code</p>
                        <p className="text-gray-800 font-mono font-bold mt-0.5">{item.pscCode}</p>
                      </div>
                      <div className="bg-gray-50 p-2 rounded-xl">
                        <p className="text-gray-400 uppercase text-[8px] font-bold tracking-wider">Region</p>
                        <p className="text-gray-800 font-bold mt-0.5">{item.placeOfPerformance}</p>
                      </div>
                      <div className="bg-gray-50 p-2 rounded-xl">
                        <p className="text-gray-400 uppercase text-[8px] font-bold tracking-wider">Set-Aside Program</p>
                        <p className="text-indigo-600 font-bold mt-0.5">{item.setAsideType}</p>
                      </div>
                    </div>

                    {/* Contact Extraction Card */}
                    {item.contactName && (
                      <div className="bg-indigo-50/40 border border-indigo-100/50 p-3 rounded-xl flex items-center justify-between gap-3 text-xs">
                        <div className="space-y-0.5">
                          <p className="text-gray-400 text-[9px] uppercase font-bold tracking-wider flex items-center gap-1">
                            <User className="w-3 h-3 text-indigo-400" />
                            Contracting Officer
                          </p>
                          <p className="font-bold text-gray-800">{item.contactName}</p>
                        </div>
                        <button
                          onClick={() => handleExtractContact(item)}
                          className="bg-white hover:bg-indigo-100/50 text-indigo-700 font-bold text-[10px] px-2.5 py-1.5 rounded border border-indigo-200 cursor-pointer flex items-center gap-1 active:scale-95 transition-all"
                        >
                          <Sparkles className="w-3 h-3 text-indigo-500 animate-pulse" />
                          Extract Contact
                        </button>
                      </div>
                    )}
                  </div>

                  {/* Actions footer */}
                  <div className="border-t border-gray-100 pt-4 mt-5 flex items-center justify-between text-xs font-bold text-gray-500">
                    <div>
                      Est. Value: <span className="text-indigo-600 text-sm font-black">${item.estimatedValue.toLocaleString()}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      {(item.sourcingSource || '').includes('Pavilion') || (item.sourcingSource || '').includes('Omnia') ? (
                        <button
                          onClick={() => handleOpenPiggybackDraft(item)}
                          className="bg-purple-600 hover:bg-purple-700 text-white px-3 py-2 rounded-xl flex items-center gap-1.5 cursor-pointer shadow-sm shadow-purple-100"
                        >
                          <FileText className="w-3.5 h-3.5" />
                          Co-op Piggyback Draft
                        </button>
                      ) : (
                        <button
                          onClick={() => handleTrackOpportunity(item)}
                          className="bg-indigo-600 hover:bg-indigo-700 text-white px-3 py-2 rounded-xl flex items-center gap-1.5 cursor-pointer shadow-sm shadow-indigo-100"
                        >
                          <Bookmark className="w-3.5 h-3.5" />
                          Track SOW
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        )}

        {currentTab === 'sam_register' && (
          <motion.div
            key="sam-register-hub"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.12 }}
            className="grid grid-cols-1 lg:grid-cols-3 gap-8"
          >
            {/* Steps Progress Checklist Sidebar */}
            <div className="bg-white p-6 rounded-2xl border border-gray-150 shadow-sm space-y-6 self-start">
              <h3 className="font-bold text-gray-900 text-lg flex items-center gap-2 pb-3 border-b border-gray-100">
                <ShieldCheck className="w-5 h-5 text-indigo-600" />
                SAM.gov Checklist
              </h3>

              <div className="space-y-4 text-xs font-semibold text-gray-500">
                <button 
                  onClick={() => setSamStep(1)}
                  className={`w-full flex items-center gap-3 p-3 rounded-xl border text-left transition-all ${
                    samStep === 1 
                      ? 'bg-indigo-50 border-indigo-200 text-indigo-950 shadow-sm' 
                      : 'border-transparent hover:bg-gray-50'
                  }`}
                >
                  <div className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-black ${
                    samForm.legalName && samForm.ein ? 'bg-emerald-500 text-white' : 'bg-indigo-100 text-indigo-700'
                  }`}>
                    {samForm.legalName && samForm.ein ? <Check className="w-3.5 h-3.5" /> : '1'}
                  </div>
                  <div>
                    <p className="font-bold">Tax &amp; Identity Setup</p>
                    <p className="text-[10px] text-gray-400 font-medium">EIN matching compliance check</p>
                  </div>
                </button>

                <button 
                  onClick={() => setSamStep(2)}
                  className={`w-full flex items-center gap-3 p-3 rounded-xl border text-left transition-all ${
                    samStep === 2 
                      ? 'bg-indigo-50 border-indigo-200 text-indigo-950 shadow-sm' 
                      : 'border-transparent hover:bg-gray-50'
                  }`}
                >
                  <div className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-black ${
                    samForm.eftRouting && samForm.eftAccount ? 'bg-emerald-500 text-white' : 'bg-indigo-100 text-indigo-700'
                  }`}>
                    {samForm.eftRouting && samForm.eftAccount ? <Check className="w-3.5 h-3.5" /> : '2'}
                  </div>
                  <div>
                    <p className="font-bold">EFT Remittance Data</p>
                    <p className="text-[10px] text-gray-400 font-medium">Direct federal funding route</p>
                  </div>
                </button>

                <button 
                  onClick={() => setSamStep(3)}
                  className={`w-full flex items-center gap-3 p-3 rounded-xl border text-left transition-all ${
                    samStep === 3 
                      ? 'bg-indigo-50 border-indigo-200 text-indigo-950 shadow-sm' 
                      : 'border-transparent hover:bg-gray-50'
                  }`}
                >
                  <div className="w-6 h-6 rounded-full bg-indigo-100 text-indigo-700 flex items-center justify-center text-[10px] font-black">
                    3
                  </div>
                  <div>
                    <p className="font-bold">Diversity Set-Asides</p>
                    <p className="text-[10px] text-gray-400 font-medium">Claim SBA preferences</p>
                  </div>
                </button>
              </div>

              {/* PDF Action */}
              <div className="pt-4 border-t border-gray-100">
                <button
                  onClick={handleDownloadSAMReport}
                  className="w-full bg-slate-800 hover:bg-slate-900 text-white font-bold text-xs py-2.5 rounded-xl flex items-center justify-center gap-2 cursor-pointer shadow-sm"
                >
                  <Download className="w-4 h-4" />
                  Download SAM Readiness PDF
                </button>
              </div>
            </div>

            {/* Input wizard body */}
            <div className="lg:col-span-2 bg-white p-8 rounded-2xl border border-gray-150 shadow-sm">
              <form onSubmit={handleSaveSamRegistration} className="space-y-6">
                {samStep === 1 && (
                  <div className="space-y-4">
                    <div className="border-b border-gray-100 pb-3">
                      <h4 className="text-base font-bold text-gray-900">Step 1: Legal Entity &amp; IRS Tax Alignment</h4>
                      <p className="text-xs text-gray-400">All information must match your state articles of incorporation and IRS Form SS-4 exactly.</p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-1">
                        <label className="text-xs text-gray-500 font-bold">Legal Entity Operating Name</label>
                        <input
                          type="text"
                          required
                          value={samForm.legalName}
                          onChange={(e) => setSamForm({...samForm, legalName: e.target.value})}
                          placeholder="e.g. Apex Engineering Services LLC"
                          className="w-full bg-gray-50 border border-gray-200 rounded-xl px-4 py-2.5 text-xs font-medium outline-none focus:bg-white focus:ring-2 focus:ring-indigo-100 focus:border-indigo-600"
                        />
                      </div>

                      <div className="space-y-1">
                        <label className="text-xs text-gray-500 font-bold">Employer Identification Number (EIN)</label>
                        <input
                          type="text"
                          required
                          value={samForm.ein}
                          onChange={(e) => setSamForm({...samForm, ein: e.target.value})}
                          placeholder="XX-XXXXXXX"
                          className="w-full bg-gray-50 border border-gray-200 rounded-xl px-4 py-2.5 text-xs font-mono font-bold outline-none focus:bg-white focus:ring-2 focus:ring-indigo-100 focus:border-indigo-600"
                        />
                      </div>
                    </div>

                    <div className="space-y-1">
                      <label className="text-xs text-gray-500 font-bold">Physical Business Street Address</label>
                      <input
                        type="text"
                        required
                        value={samForm.address}
                        onChange={(e) => setSamForm({...samForm, address: e.target.value})}
                        placeholder="e.g. 100 Innovation Parkway, Suite B"
                        className="w-full bg-gray-50 border border-gray-200 rounded-xl px-4 py-2.5 text-xs font-medium outline-none focus:bg-white focus:ring-2 focus:ring-indigo-100"
                      />
                    </div>

                    <div className="grid grid-cols-3 gap-3">
                      <div className="space-y-1">
                        <label className="text-xs text-gray-500 font-bold">City</label>
                        <input
                          type="text"
                          required
                          value={samForm.city}
                          onChange={(e) => setSamForm({...samForm, city: e.target.value})}
                          placeholder="Sunnyvale"
                          className="w-full bg-gray-50 border border-gray-200 rounded-xl px-4 py-2.5 text-xs font-medium outline-none focus:bg-white"
                        />
                      </div>
                      <div className="space-y-1">
                        <label className="text-xs text-gray-500 font-bold">State</label>
                        <input
                          type="text"
                          required
                          value={samForm.state}
                          onChange={(e) => setSamForm({...samForm, state: e.target.value})}
                          placeholder="CA"
                          className="w-full bg-gray-50 border border-gray-200 rounded-xl px-4 py-2.5 text-xs font-medium outline-none focus:bg-white"
                        />
                      </div>
                      <div className="space-y-1">
                        <label className="text-xs text-gray-500 font-bold">ZIP Code</label>
                        <input
                          type="text"
                          required
                          value={samForm.zip}
                          onChange={(e) => setSamForm({...samForm, zip: e.target.value})}
                          placeholder="94089"
                          className="w-full bg-gray-50 border border-gray-200 rounded-xl px-4 py-2.5 text-xs font-medium outline-none focus:bg-white"
                        />
                      </div>
                    </div>
                  </div>
                )}

                {samStep === 2 && (
                  <div className="space-y-4">
                    <div className="border-b border-gray-100 pb-3">
                      <h4 className="text-base font-bold text-gray-900">Step 2: Financial Remittance (EFT) Setup</h4>
                      <p className="text-xs text-gray-400">Public funds are disbursed exclusively via Electronic Funds Transfer (EFT). These routing details secure direct deposit alignment upon RFP awards.</p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-1">
                        <label className="text-xs text-gray-500 font-bold">Direct Deposit Routing Number</label>
                        <input
                          type="text"
                          required
                          value={samForm.eftRouting}
                          onChange={(e) => setSamForm({...samForm, eftRouting: e.target.value})}
                          placeholder="9-digit ACH Routing"
                          className="w-full bg-gray-50 border border-gray-200 rounded-xl px-4 py-2.5 text-xs font-mono outline-none focus:bg-white focus:ring-2 focus:ring-indigo-100"
                        />
                      </div>

                      <div className="space-y-1">
                        <label className="text-xs text-gray-500 font-bold">Remittance Bank Account Number</label>
                        <input
                          type="text"
                          required
                          value={samForm.eftAccount}
                          onChange={(e) => setSamForm({...samForm, eftAccount: e.target.value})}
                          placeholder="Business checking account"
                          className="w-full bg-gray-50 border border-gray-200 rounded-xl px-4 py-2.5 text-xs font-mono outline-none focus:bg-white focus:ring-2 focus:ring-indigo-100"
                        />
                      </div>
                    </div>
                  </div>
                )}

                {samStep === 3 && (
                  <div className="space-y-4">
                    <div className="border-b border-gray-100 pb-3">
                      <h4 className="text-base font-bold text-gray-900">Step 3: Diversity &amp; Small Business Set-Asides</h4>
                      <p className="text-xs text-gray-400">Claim official SBA preferences to bypass large prime competition and participate in exclusive federal set-aside contracts.</p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <label className="flex items-start gap-3 p-4 rounded-xl border border-gray-100 hover:border-indigo-100 cursor-pointer text-xs select-none">
                        <input 
                          type="checkbox" 
                          checked={samForm.certifiedWosb} 
                          onChange={(e) => setSamForm({...samForm, certifiedWosb: e.target.checked})} 
                          className="mt-0.5 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                        />
                        <div>
                          <p className="font-bold text-gray-800">Women-Owned Small Business (WOSB)</p>
                          <p className="text-[10px] text-gray-400 mt-0.5 font-medium">SBA certified and eligible for targeted 5% set-asides.</p>
                        </div>
                      </label>

                      <label className="flex items-start gap-3 p-4 rounded-xl border border-gray-100 hover:border-indigo-100 cursor-pointer text-xs select-none">
                        <input 
                          type="checkbox" 
                          checked={samForm.certifiedSdvosb} 
                          onChange={(e) => setSamForm({...samForm, certifiedSdvosb: e.target.checked})} 
                          className="mt-0.5 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                        />
                        <div>
                          <p className="font-bold text-gray-800">Service-Disabled Veteran-Owned (SDVOSB)</p>
                          <p className="text-[10px] text-gray-400 mt-0.5 font-medium">Bypass standard limits and qualify for veteran preferences.</p>
                        </div>
                      </label>

                      <label className="flex items-start gap-3 p-4 rounded-xl border border-gray-100 hover:border-indigo-100 cursor-pointer text-xs select-none">
                        <input 
                          type="checkbox" 
                          checked={samForm.certifiedHubzone} 
                          onChange={(e) => setSamForm({...samForm, certifiedHubzone: e.target.checked})} 
                          className="mt-0.5 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                        />
                        <div>
                          <p className="font-bold text-gray-800">HUBZone Certified</p>
                          <p className="text-[10px] text-gray-400 mt-0.5 font-medium">Historically Underutilized Business Zone certification advantage.</p>
                        </div>
                      </label>

                      <label className="flex items-start gap-3 p-4 rounded-xl border border-gray-100 hover:border-indigo-100 cursor-pointer text-xs select-none">
                        <input 
                          type="checkbox" 
                          checked={samForm.certified8a} 
                          onChange={(e) => setSamForm({...samForm, certified8a: e.target.checked})} 
                          className="mt-0.5 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                        />
                        <div>
                          <p className="font-bold text-gray-800">SBA 8(a) Development Program</p>
                          <p className="text-[10px] text-gray-400 mt-0.5 font-medium">9-year program for socially/economically disadvantaged owners.</p>
                        </div>
                      </label>
                    </div>
                  </div>
                )}

                {/* Footer buttons */}
                <div className="pt-6 border-t border-gray-100 flex items-center justify-between">
                  <button
                    type="button"
                    disabled={samStep === 1}
                    onClick={() => setSamStep(prev => prev - 1)}
                    className="bg-gray-100 hover:bg-gray-200 disabled:opacity-50 text-gray-700 text-xs font-bold px-4 py-2 rounded-xl cursor-pointer"
                  >
                    Back
                  </button>
                  <div className="flex gap-2">
                    {samStep < 3 ? (
                      <button
                        type="button"
                        onClick={() => setSamStep(prev => prev + 1)}
                        className="bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-bold px-5 py-2 rounded-xl cursor-pointer"
                      >
                        Next Step
                      </button>
                    ) : (
                      <button
                        type="submit"
                        className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold px-5 py-2 rounded-xl cursor-pointer flex items-center gap-1"
                      >
                        <Check className="w-4 h-4" />
                        Save &amp; Generate UEI Pack
                      </button>
                    )}
                  </div>
                </div>
              </form>

              {samSaved && (
                <div className="mt-4 bg-emerald-50 border border-emerald-100 text-emerald-800 text-xs p-4 rounded-xl flex items-center gap-2">
                  <CheckSquare className="w-5 h-5 text-emerald-600" />
                  <div>
                    <span className="font-bold">Federal Profile Synced!</span> Your information has been synced successfully with your Core Vendor Profile and formatted for SAM.gov validation.
                  </div>
                </div>
              )}
            </div>
          </motion.div>
        )}

        {currentTab === 'standards_exp' && (
          <motion.div
            key="standards-explorer"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.12 }}
            className="space-y-6"
          >
            {/* SBA Size Calculator Card */}
            <div className="bg-white p-6 rounded-2xl border border-gray-150 shadow-sm grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className="space-y-3">
                <h3 className="text-base font-black text-gray-900 flex items-center gap-1.5">
                  <TrendingUp className="text-indigo-600 w-5 h-5" />
                  SBA Size Standards Evaluator
                </h3>
                <p className="text-xs text-gray-500 leading-relaxed">
                  Enter your industry details to check if your business qualifies under statutory limits to participate in lucrative, simplified set-aside opportunities.
                </p>

                <div className="space-y-3 pt-3">
                  <div className="space-y-1">
                    <label className="text-[10px] text-gray-500 font-bold uppercase tracking-wider">Target NAICS Code</label>
                    <select
                      value={calcNaics}
                      onChange={(e) => setCalcNaics(e.target.value)}
                      className="w-full bg-gray-50 border border-gray-200 rounded-xl px-3 py-2 text-xs font-medium cursor-pointer"
                    >
                      {POPULAR_NAICS.map(n => (
                        <option key={n.code} value={n.code}>{n.code} - {n.title.substring(0, 35)}...</option>
                      ))}
                    </select>
                  </div>

                  <div className="space-y-1">
                    <label className="text-[10px] text-gray-500 font-bold uppercase tracking-wider">Average Annual Receipts ($)</label>
                    <input
                      type="number"
                      value={calcReceipts}
                      onChange={(e) => setCalcReceipts(e.target.value)}
                      className="w-full bg-gray-50 border border-gray-200 rounded-xl px-3 py-2 text-xs font-medium outline-none"
                    />
                  </div>

                  <div className="space-y-1">
                    <label className="text-[10px] text-gray-500 font-bold uppercase tracking-wider">Number of Employees</label>
                    <input
                      type="number"
                      value={calcEmployees}
                      onChange={(e) => setCalcEmployees(e.target.value)}
                      className="w-full bg-gray-50 border border-gray-200 rounded-xl px-3 py-2 text-xs font-medium outline-none"
                    />
                  </div>

                  <button
                    onClick={handleCalculateSizeStandard}
                    className="w-full bg-indigo-600 hover:bg-indigo-700 text-white font-bold text-xs py-2 rounded-xl cursor-pointer"
                  >
                    Evaluate Eligibility
                  </button>
                </div>
              </div>

              {/* Calculator Results Display */}
              <div className="lg:col-span-2 bg-gray-50 p-6 rounded-2xl flex flex-col justify-between border border-gray-100">
                {sizeResult ? (
                  <div className="space-y-4">
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-xl flex items-center justify-center font-bold ${
                        sizeResult.isSmall ? 'bg-emerald-100 text-emerald-800' : 'bg-rose-100 text-rose-800'
                      }`}>
                        {sizeResult.isSmall ? <Check className="w-5 h-5" /> : <AlertTriangle className="w-5 h-5" />}
                      </div>
                      <div>
                        <p className="text-xs text-gray-400 font-medium">Eligibility Decision</p>
                        <h4 className="text-base font-black text-gray-900">
                          {sizeResult.isSmall ? 'QUALIFIED SMALL BUSINESS' : 'CLASSIFIED AS LARGE CONTRACTOR'}
                        </h4>
                      </div>
                    </div>

                    <div className="space-y-2">
                      <div className="flex justify-between text-xs pb-1 border-b border-gray-200">
                        <span className="text-gray-400 font-medium">Industry Size Threshold</span>
                        <span className="font-bold text-gray-800">{sizeResult.standardLimit}</span>
                      </div>
                      <p className="text-xs text-gray-500 leading-relaxed font-medium bg-white p-3 rounded-xl border border-gray-100 mt-2">
                        {sizeResult.explanation}
                      </p>
                    </div>

                    <div className="bg-indigo-50 border border-indigo-100 p-3 rounded-xl text-[10px] text-indigo-800 leading-relaxed font-semibold">
                      Pro Tip: Small businesses can leverage diversity certifications (WOSB, 8a, HUBZone) to gain 10% bid preference scoring modifiers in federal bidding!
                    </div>
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center text-center h-full space-y-2 py-8 text-gray-400">
                    <TrendingUp className="w-10 h-10 text-gray-300" />
                    <p className="text-xs font-bold">Awaiting Valuation Parameters</p>
                    <p className="text-[11px] text-gray-400 max-w-xs">Enter your revenue data and choose your primary NAICS industry selector to verify eligibility results.</p>
                  </div>
                )}
              </div>
            </div>

            {/* Threshold References Row */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="bg-white p-5 rounded-2xl border border-gray-150 shadow-sm space-y-2">
                <span className="text-[10px] bg-sky-50 text-sky-800 font-bold px-2 py-0.5 rounded uppercase">Under $10,000</span>
                <h4 className="font-black text-gray-900 text-sm">Micro-Purchase Threshold</h4>
                <p className="text-xs text-gray-500 leading-relaxed">
                  Federal procurement items valued below $10,000 can be acquired directly from suppliers bypassing complex formal RFPs. Highly ideal for small local contractors.
                </p>
              </div>

              <div className="bg-white p-5 rounded-2xl border border-gray-150 shadow-sm space-y-2">
                <span className="text-[10px] bg-indigo-50 text-indigo-800 font-bold px-2 py-0.5 rounded uppercase">$10k to $250k</span>
                <h4 className="font-black text-gray-900 text-sm">Simplified Acquisition Threshold</h4>
                <p className="text-xs text-gray-500 leading-relaxed">
                  Solicitations valued under $250,000 are reserved exclusively for qualified small businesses, allowing simpler proposal templates with quicker award timelines.
                </p>
              </div>

              <div className="bg-white p-5 rounded-2xl border border-gray-150 shadow-sm space-y-2">
                <span className="text-[10px] bg-purple-50 text-purple-800 font-bold px-2 py-0.5 rounded uppercase">Active Catalog</span>
                <h4 className="font-black text-gray-900 text-sm">GSA Schedules (MAS)</h4>
                <p className="text-xs text-gray-500 leading-relaxed">
                  A long-term government-wide contract allowing federal personnel to purchase pre-negotiated commercial services instantly. Acts as the primary supplier badge.
                </p>
              </div>
            </div>

            {/* Code lookup directories */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* NAICS database list */}
              <div className="bg-white p-6 rounded-2xl border border-gray-150 shadow-sm space-y-4">
                <h3 className="font-bold text-gray-900 text-base flex items-center gap-1.5 border-b border-gray-100 pb-3">
                  <Layers2 className="w-5 h-5 text-indigo-600" />
                  Popular NAICS Procurement Codes
                </h3>

                <div className="space-y-3 max-h-72 overflow-y-auto pr-2">
                  {POPULAR_NAICS.map(naics => (
                    <div key={naics.code} className="flex justify-between items-start p-2.5 hover:bg-gray-50 rounded-xl text-xs border border-transparent hover:border-gray-100">
                      <div>
                        <p className="font-bold text-gray-800">{naics.code}</p>
                        <p className="text-gray-500 text-[11px] mt-0.5 font-medium">{naics.title}</p>
                      </div>
                      <span className="text-[10px] text-indigo-600 bg-indigo-50 px-2 py-0.5 rounded font-mono font-bold shrink-0">
                        Limit: {naics.sizeStandard}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              {/* PSC Directory */}
              <div className="bg-white p-6 rounded-2xl border border-gray-150 shadow-sm space-y-4">
                <h3 className="font-bold text-gray-900 text-base flex items-center gap-1.5 border-b border-gray-100 pb-3">
                  <Layers className="w-5 h-5 text-purple-600" />
                  Primary Federal PSC Code Map
                </h3>

                <div className="space-y-3 max-h-72 overflow-y-auto pr-2">
                  {POPULAR_PSC.map(psc => (
                    <div key={psc.code} className="p-2.5 hover:bg-gray-50 rounded-xl text-xs border border-transparent hover:border-gray-100 flex gap-3">
                      <span className="text-[10px] font-mono font-bold text-purple-700 bg-purple-50 px-2 py-1 rounded h-fit shrink-0">
                        {psc.code}
                      </span>
                      <div>
                        <p className="text-gray-800 font-bold">{psc.title}</p>
                        <p className="text-gray-400 text-[10px] mt-0.5 font-medium">Mapped to Standard Defense Logistics catalog codes</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </motion.div>
        )}

        {currentTab === 'crm_contacts' && (
          <motion.div
            key="crm-contacts"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.12 }}
            className="space-y-6"
          >
            {/* Contacts Table View */}
            <div className="bg-white rounded-2xl border border-gray-150 shadow-sm overflow-hidden">
              <div className="p-6 border-b border-gray-100 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                  <h3 className="text-base font-black text-gray-900">Procurement Officer CRM Database</h3>
                  <p className="text-xs text-gray-400">Manage contact details extracted from civilian federal, military, state, and co-operative bids.</p>
                </div>

                <div className="flex items-center gap-2">
                  <span className="text-xs font-mono text-gray-400 bg-gray-50 px-2.5 py-1 rounded border border-gray-100">
                    Contacts Tracked: {crmContacts.length}
                  </span>
                </div>
              </div>

              {contactsLoading ? (
                <div className="p-16 text-center space-y-3">
                  <RefreshCw className="w-8 h-8 text-indigo-500 animate-spin mx-auto" />
                  <p className="text-xs text-gray-500 font-semibold">Loading CRM points of contact...</p>
                </div>
              ) : crmContacts.length === 0 ? (
                <div className="p-16 text-center space-y-4">
                  <div className="w-12 h-12 bg-indigo-50 rounded-full flex items-center justify-center text-indigo-500 mx-auto">
                    <User className="w-6 h-6" />
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-800 text-sm">No Extracted Contacts</h4>
                    <p className="text-xs text-gray-400 max-w-sm mx-auto mt-1">
                      Go to "Federal &amp; Co-op Bids" tab and click "Extract Contact" on any solicitation card to populate this Procurement CRM.
                    </p>
                  </div>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-left border-collapse text-xs">
                    <thead>
                      <tr className="bg-gray-50 border-b border-gray-100 text-gray-400 uppercase font-black tracking-wider text-[9px]">
                        <th className="p-4">Contact Officer</th>
                        <th className="p-4">Agency / Commission</th>
                        <th className="p-4">Origin RFP SOW</th>
                        <th className="p-4">Contact Credentials</th>
                        <th className="p-4 text-right">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100 font-medium text-gray-700">
                      {crmContacts.map(contact => (
                        <tr key={contact.id} className="hover:bg-gray-50/50">
                          <td className="p-4">
                            <div className="font-bold text-gray-900">{contact.name}</div>
                            <div className="text-gray-400 text-[10px] mt-0.5">ID: {contact.id}</div>
                          </td>
                          <td className="p-4">
                            <span className="bg-slate-100 text-slate-800 font-bold px-2 py-0.5 rounded text-[10px] uppercase">
                              {contact.agency}
                            </span>
                          </td>
                          <td className="p-4 max-w-xs">
                            <div className="truncate font-bold text-gray-800">{contact.sourceRfxTitle}</div>
                            <div className="text-[10px] font-mono text-indigo-600 mt-0.5">Sol. #{contact.sourceRfxNumber}</div>
                          </td>
                          <td className="p-4 space-y-1">
                            <div className="flex items-center gap-1.5">
                              <Mail className="w-3.5 h-3.5 text-gray-400 shrink-0" />
                              <span className="font-semibold text-gray-800">{contact.email}</span>
                            </div>
                            <div className="flex items-center gap-1.5 text-gray-400">
                              <Phone className="w-3.5 h-3.5 shrink-0" />
                              <span>{contact.phone}</span>
                            </div>
                          </td>
                          <td className="p-4 text-right">
                            <div className="flex items-center justify-end gap-2">
                              <button
                                onClick={() => handleLaunchEmail(contact)}
                                className="bg-indigo-50 hover:bg-indigo-100 text-indigo-700 p-2 rounded-xl border border-indigo-100 cursor-pointer"
                                title="Compose capability statement inquiry"
                              >
                                <Send className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => handleDeleteContact(contact.id)}
                                className="bg-rose-50 hover:bg-rose-100 text-rose-700 p-2 rounded-xl border border-rose-100 cursor-pointer"
                                title="Delete Contact"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Piggyback Agreement Draft Modal */}
      {showPiggybackModal && selectedCoopRfx && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white w-full max-w-3xl rounded-3xl border border-gray-100 overflow-hidden shadow-2xl flex flex-col max-h-[90vh]">
            {/* Header */}
            <div className="bg-indigo-950 text-white p-6 flex justify-between items-center">
              <div>
                <span className="text-xs bg-indigo-500/20 text-indigo-200 border border-indigo-500/30 font-bold px-2.5 py-1 rounded-full uppercase tracking-wider">
                  Cooperative Sourcing Assistant
                </span>
                <h3 className="text-lg font-black tracking-tight mt-1.5">Co-op Piggybacking Agreement Generator</h3>
              </div>
              <button 
                onClick={() => setShowPiggybackModal(false)}
                className="text-indigo-200 hover:text-white p-2 rounded-xl hover:bg-indigo-900"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Inputs & Preview Body */}
            <div className="p-8 space-y-6 overflow-y-auto flex-1">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-1">
                  <label className="text-xs text-gray-500 font-bold">Your Company Legal Name</label>
                  <input
                    type="text"
                    value={vendorCompany}
                    onChange={(e) => setVendorCompany(e.target.value)}
                    className="w-full bg-gray-50 border border-gray-200 rounded-xl px-4 py-2.5 text-xs font-semibold outline-none focus:bg-white"
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-xs text-gray-500 font-bold">Target Local Procuring Agency</label>
                  <input
                    type="text"
                    value={procuringAgency}
                    onChange={(e) => setProcuringAgency(e.target.value)}
                    className="w-full bg-gray-50 border border-gray-200 rounded-xl px-4 py-2.5 text-xs font-semibold outline-none focus:bg-white"
                  />
                </div>
              </div>

              {/* Draft Preview Board */}
              <div className="space-y-2">
                <label className="text-xs text-gray-500 font-bold uppercase tracking-wider">Piggybacking Agreement Draft Preview</label>
                <textarea
                  value={piggybackDraft
                    .replace("[Vendor Company Name]", vendorCompany)
                    .replace("[Procuring Agency Name]", procuringAgency)}
                  onChange={(e) => setPiggybackDraft(e.target.value)}
                  rows={10}
                  className="w-full bg-gray-50 border border-gray-200 p-4 rounded-xl text-xs font-mono leading-relaxed outline-none focus:bg-white"
                />
              </div>
            </div>

            {/* Footer */}
            <div className="bg-gray-50 px-8 py-4 border-t border-gray-150 flex justify-between items-center shrink-0">
              <p className="text-[10px] text-gray-400 font-medium">Agreement draft aligns to Pavilion &amp; Omnia piggybacking clauses.</p>
              <div className="flex gap-2">
                <button
                  onClick={() => setShowPiggybackModal(false)}
                  className="bg-white hover:bg-gray-100 text-gray-700 text-xs font-bold px-4 py-2 rounded-xl border border-gray-200 cursor-pointer"
                >
                  Cancel
                </button>
                <button
                  onClick={handleExportPiggybackPDF}
                  className="bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-bold px-4 py-2 rounded-xl cursor-pointer flex items-center gap-1.5"
                >
                  <Download className="w-4 h-4" />
                  Download Piggyback Proposal PDF
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Gmail Sender Modal */}
      {showEmailModal && selectedCrmContact && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white w-full max-w-xl rounded-3xl border border-gray-100 overflow-hidden shadow-2xl flex flex-col">
            {/* Header */}
            <div className="bg-indigo-950 text-white p-6 flex justify-between items-center">
              <h3 className="font-bold text-base flex items-center gap-2">
                <Mail className="w-5 h-5 text-indigo-400" />
                Compose Capability Query
              </h3>
              <button 
                onClick={() => setShowEmailModal(false)}
                className="text-indigo-200 hover:text-white"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Form */}
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4 text-xs font-medium text-gray-500">
                <div>
                  <span className="block text-gray-400">Recipient Name</span>
                  <span className="font-bold text-gray-800">{selectedCrmContact.name}</span>
                </div>
                <div>
                  <span className="block text-gray-400">Recipient Email</span>
                  <span className="font-bold text-indigo-600 font-mono">{selectedCrmContact.email}</span>
                </div>
              </div>

              <div className="space-y-1">
                <label className="text-xs text-gray-500 font-bold">Email Subject</label>
                <input
                  type="text"
                  value={emailSubject}
                  onChange={(e) => setEmailSubject(e.target.value)}
                  className="w-full bg-gray-50 border border-gray-200 rounded-xl px-4 py-2 text-xs font-semibold outline-none focus:bg-white"
                />
              </div>

              <div className="space-y-1">
                <label className="text-xs text-gray-500 font-bold">Message Content</label>
                <textarea
                  value={emailBody}
                  onChange={(e) => setEmailBody(e.target.value)}
                  rows={8}
                  className="w-full bg-gray-50 border border-gray-200 p-4 rounded-xl text-xs font-medium leading-relaxed outline-none focus:bg-white"
                />
              </div>
            </div>

            {/* Footer */}
            <div className="bg-gray-50 px-6 py-4 border-t border-gray-100 flex justify-end gap-2">
              <button
                onClick={() => setShowEmailModal(false)}
                className="bg-white hover:bg-gray-100 text-gray-700 text-xs font-bold px-4 py-2 rounded-xl border border-gray-200"
              >
                Cancel
              </button>
              <button
                onClick={handleSendEmail}
                disabled={sendingEmail}
                className="bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-bold px-5 py-2 rounded-xl flex items-center gap-1.5 cursor-pointer"
              >
                {sendingEmail ? (
                  <>
                    <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                    Sending Inquiry...
                  </>
                ) : (
                  <>
                    <Send className="w-3.5 h-3.5" />
                    Dispatch Email
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
